import { Fragment, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  api_key,
  authEmployeeID,
  sdkURL,
  secret_key,
  separateBase64Image,
} from "../utils/constants";
import { ReactComponent as Upload } from "../assets/icons/Upload.svg";
import { ReactComponent as Close } from "../assets/icons/Close.svg";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import FaceScanImg from "../assets/images/face-scan-img.png";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const data = reader.result;
      if (typeof data === "string" && data.startsWith("data:image/"))
        resolve(reader.result);
      else reject(new Error("Invalid file."));
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });

const AllScanInstructions = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const isFaceMatchRequired = useMemo(
    () => state?.selectedAssesments?.faceMatch === true,
    [state]
  );
  const inputRef = useRef(null);
  const [picture, setPicture] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectImage = async (e) => {
    const file = e.target.files[0];
    if (file)
      try {
        if (file.size > 2097152)
          throw new Error("File size exceeds the limit.");
        const b64data = await fileToBase64(file);
        setPicture(b64data);
      } catch (err) {
        console.error(err);
        setError("Please select a valid image file. (Max size: 2MB)");
      }
  };

  const resetImage = () => {
    setPicture("");
    if (inputRef.current) inputRef.current.value = null;
  };

  const startScan = async () => {
    try {
      window?.ReactNativeWebView?.postMessage?.("prompt-camera-permission");
      setLoading(true);
      if (isFaceMatchRequired && picture.length === 0)
        throw new Error("Please upload a Gover ID to proceed.");
      const scanTokenResp = await fetch(sdkURL("/vitals/create-token"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: secret_key,
        },
        body: JSON.stringify({
          api_key,
          employee_id: authEmployeeID,
          ...(isFaceMatchRequired
            ? {
              is_facematch_required: true,
              base_image: separateBase64Image(picture),
            }
            : {}),
        }),
      });
      const scanTokenRespJSON = await scanTokenResp.json();
      if (scanTokenRespJSON?.statusCode?.toString().startsWith("2"))
        navigate("/all-combined-face-scan", {
          state: {
            ...state,
            faceMatchToken: scanTokenRespJSON.facematch_token,
            scanToken: scanTokenRespJSON.scan_token,
            scanTokenExpiry: new Date(
              scanTokenRespJSON.token_expiry?.replace(" ", "T")
            ).getTime(),
            videoIndex:
              state?.selectedAssesments?.vision === true
                ? Math.floor(Math.random() * 3 + 1)
                : undefined,
          },
        });
      else
        throw new Error(
          scanTokenRespJSON?.message ?? "Error in Fetching the Scan Token"
        );
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-8 pt-2 pb-24 relative tab-screen">
      <div className="flex items-center justify-center relative mb-8 xl:justify-start">
        <Link
          to={-1}
          className=" w-9 h-9 bg-[#2D2D2D] rounded-full absolute left-0 flex flex-row justify-center items-center"
        >
          <img src={BackArrow} alt="" />
        </Link>
        <h2 className="text-xl font-medium text-white lg:text-2xl md:text-2xl xl:ml-14">
          Instructions
        </h2>
      </div>
      <div className="circle-blur w-[70%] h-56 mx-auto absolute top-24 left-0 right-0 blur-[100px]" />
      <div className="relative z-50">
        <img src={FaceScanImg} className="w-28 mx-auto mb-3" alt="Face Scan" />
        <div className="bg-[#1D1D1D]/70 rounded-[20px] p-6 lg:mt-6 md:mt-6">
          <h6 className=" text-base font-semibold text-secondary mb-3">
            Face Scan
          </h6>
          <div className="ml-0">
            <ol
              type="1"
              className="list-outside list-item text-xxs text-white font-light p-0 m-0"
            >
              <li className="mb-3 flex flex-row">
                <span className="mr-[2px]">1.</span> The ambient light should be
                bright and consistent and must be white.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">2.</span>Remove your masks, eye-wear,
                or clothing obstruction the face for the duration of scan.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">3.</span>Keep your face and phone
                steady to avoid movements during the scan.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">4.</span>For light mode: Make sure
                your face is mostly covered by the blue circle.
              </li>
              <li className="text-xxs text-white font-light mb-3 flex flex-row">
                <span className="mr-[2px]">5.</span>If prompted, allow the
                camera.
              </li>
            </ol>
          </div>
        </div>
      </div>

      {isFaceMatchRequired && (
        <>
          <h4 className="mt-6 text-lg font-semibold text-white">
            Upload any Government ID
          </h4>
          <div className="mt-4">
            <input
              ref={inputRef}
              className="sr-only"
              type="file"
              id="government-id"
              name="government-id"
              accept=".jpg, .jpeg, .png"
              onChange={selectImage}
              disabled={loading}
            />
            {picture.length > 0 ? (
              <div className="h-40 max-w-max relative">
                <img
                  className="h-full object-fill rounded-xl"
                  src={picture}
                  alt="government-id"
                />
                <button
                  className="absolute -top-3 -right-3 h-6 w-6 p-0.5 rounded-full bg-error text-white"
                  type="button"
                  onClick={resetImage}
                  disabled={loading}
                >
                  <span className="sr-only">Clear</span>
                  <Close className="h-full w-full" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="government-id"
                className="h-40 p-4 flex flex-col justify-evenly items-center text-center rounded-xl  bg-[#1D1D1D]"
              >
                <Upload className="shrink-0 h-16 w-16" />
                <div>
                  <p className="text-xs">Upload Documents</p>
                  <p className="opacity-70 text-xxs font-light">
                    (Only .jpg/.jpeg/.png files less than 10MB are accepted)
                  </p>
                </div>
              </label>
            )}
          </div>
        </>
      )}

      <button
        className="mt-6 w-full flex items-center font-medium justify-center space-x-4 rounded-full outline-none px-5 py-2.5 bg-white disabled:bg-none disabled:bg-darkgray text-black mb-2"
        type="button"
        onClick={startScan}
        disabled={loading}
      >
        <span>Proceed</span>
        {loading && <Loader className="flex-shrink-0 h-4 w-4" />}
      </button>

      <Transition show={error.length > 0} as={Fragment}>
        <Dialog as={Fragment} onClose={() => setError("")}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/70 flex items-center justify-center"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col text-center">
                <Dialog.Description className="text-black font-normal text-sm">
                  {error}
                </Dialog.Description>
                <button
                  className="block mt-6 mx-auto px-4 py-2 rounded-full bg-gradient text-white text-xs font-medium"
                  type="button"
                  onClick={() => setError("")}
                >
                  Ok
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>

      <div className="mt-6 p-5 rounded-[20px] bg-[#364560] text-white space-y-1.5">
        <h3 className="text-sm font-medium text-secondary">Disclaimer</h3>
        <p className="text-xxs text-justify font-light leading-4 text-white/80">
          For Investigational Use Only. Careplix is not a substitute for the
          clinical judgment of a health care professional. Careplix is intended
          to improve your awareness of general wellness. Careplix does not
          diagnose, treat, mitigate or prevent any disease, symptom, disorder or
          abnormal physical state. Consult with a health care professional or
          emergency services if you believe you may have a medical issue.
        </p>
      </div>
    </section>
  );
};

export default AllScanInstructions;
